import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  COMPANY_INFO,
  BENEFITS,
  FLISERENS_PACKAGES
} from "../data/siteData";
import FliserensCalculator from "../components/FliserensCalculator";
import ReviewSlider from "../components/ReviewSlider";
import CmsGallery from "../components/CmsGallery";

export default function FliserensPage() {
  const [, setCalculationData] = useState(null);

  const benefits = [
    {
      icon: "fa-location-dot",
      title: "Lokalt på Vestsjælland",
      desc: "Vi kender området og er hurtigt fremme"
    },
    {
      icon: "fa-bolt",
      title: "Hurtig tid",
      desc: "Fleksible aftaler der passer til dig"
    },
    {
      icon: "fa-tag",
      title: "Skarpe priser",
      desc: "Gennemsigtig prisfastsættelse uden skjulte gebyrer"
    },
    {
      icon: "fa-circle-check",
      title: "Professionelt resultat",
      desc: "Kvalitet og grundighed – hver gang"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Fliserens Slagelse | Professionel rens af fliser</title>
        <meta
          name="description"
          content="Få professionel fliserens i Slagelse og omegn. Vi fjerner alger, mos og snavs og giver dine fliser nyt liv. Få et uforpligtende tilbud i dag."
        />
        <meta
          property="og:title"
          content="Fliserens Slagelse | Professionel rens af fliser"
        />
        <meta
          property="og:description"
          content="Få professionel fliserens i Slagelse og omegn. Vi fjerner alger, mos og snavs og giver dine fliser nyt liv. Få et uforpligtende tilbud i dag."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fliserens-have-terrasse-ren.png"
        />
        <link rel="canonical" href="https://nf-polering.dk/fliserens" />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-slate-50 text-slate-900 pt-12 pb-20 lg:pt-16 lg:pb-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Fliserens fra kun 29 kr. pr. m² inkl. imprægnering
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Få hurtig tid, skarpe priser og et resultat der holder. Ring nu eller få dit uforpligtende tilbud med det samme.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-base transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring og få en pris</span>
                </a>
                <a
                  href="#beregner"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white hover:bg-slate-100 text-slate-900 font-semibold text-base border border-slate-300 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 whitespace-nowrap shadow-sm"
                >
                  <i className="fa-solid fa-calculator text-slate-800" aria-hidden="true" />
                  <span>Beregn din pris</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-none overflow-hidden shadow-xl border border-slate-200 bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fliserens-have-terrasse-ren.png"
                  alt="Professionel fliserens"
                  className="w-full h-80 sm:h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 sm:py-14 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 text-center">
            {benefits.map((benefit, i) => (
              <div
                key={i}
                className="flex flex-col items-center p-2"
              >
                <div className="w-12 h-12 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600 mb-3 text-lg">
                  <i className={`fa-solid ${benefit.icon}`} aria-hidden="true" />
                </div>
                <h3 className="font-bold text-slate-900 text-base leading-tight">
                  {benefit.title}
                </h3>
                <p className="mt-1.5 text-slate-600 text-sm leading-relaxed w-full mx-auto">
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fliserens Packages Pricing Table */}
      <section className="py-16 sm:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Fliserens Pakker &amp; Priser
            </h2>
            <p className="mt-3 text-slate-600 text-base">
              Gennemsigtige priser – så du ved præcis hvad det koster
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {FLISERENS_PACKAGES.map((pkg) => (
              <div
                key={pkg.id}
                className="relative rounded-none p-7 sm:p-8 flex flex-col justify-between transition-all bg-white border border-slate-200 shadow-sm"
              >
                <div>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-4">
                    {pkg.name}
                  </h3>

                  <div className="flex items-baseline gap-1.5 mb-4 pb-4 border-b border-slate-100">
                    <span className="text-3xl sm:text-4xl font-black text-slate-900">
                      {pkg.pricePerM2},-
                    </span>
                    <span className="text-sm font-semibold text-slate-500">
                      Pr. m²
                    </span>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-600 mb-6 italic">
                    {pkg.description}
                  </p>

                  <ul className="space-y-3 mb-8 text-sm text-slate-700">
                    {pkg.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5">
                        <i className="fa-solid fa-check text-cyan-600 text-sm mt-0.5 shrink-0" aria-hidden="true" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <a
                    href={COMPANY_INFO.phoneTel}
                    className="w-full py-3.5 px-4 rounded-full font-bold text-sm text-center transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95 flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <span>FÅ TILBUD</span>
                    <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto font-medium">
            Alle priser er inkl. moms.
          </p>
        </div>
      </section>

      {/* Interactive Price Calculator Section */}
      <section id="tilbud" className="py-16 sm:py-24 bg-slate-50 border-t border-slate-200 scroll-mt-24 sm:scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FliserensCalculator onSelectCalculation={(data) => setCalculationData(data)} />
        </div>
      </section>

      {/* Verbatim Content Section - Same layout as CityPage */}
      <section className="py-16 sm:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 sm:space-y-32">
          {/* Row 1: Image Left, Text Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 lg:order-2 space-y-3.5 text-slate-700 leading-relaxed text-base">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                Professionel fliserens i Slagelse, Korsør, Vemmelev, Skælskør, Sorø, Høng, Stillinge og omegn
              </h2>
              <p>
                Er dine fliser blevet mørke, grønne eller fyldt med alger og belægninger? Hos <strong>NF-Polering</strong> hjælper vi med at få dine udendørs fliser til at fremstå flotte og velholdte igen.
              </p>
              <p className="font-semibold text-slate-900">
                Lige nu tilbyder vi professionel fliserens fra kun 29 kr. pr. m² inkl. algebehandling og imprægnering.
              </p>
              <p>
                Det betyder, at du får mere end bare rensning af fliserne. Efter behandlingen imprægnerer vi overfladen, så fliserne får en beskyttende behandling efter rensningen.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-slate-900 font-semibold text-sm">
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Fliserens</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Algebehandling</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Imprægnering</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Kun 29 kr. pr. m²</span>
                </li>
              </ul>

              <p className="pt-2 font-medium text-slate-900">
                Få et gratis og uforpligtende tilbud på dine fliser i dag.
              </p>

              <div className="pt-3">
                <a
                  href="tel:30146070"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 lg:order-1">
              <div className="rounded-none overflow-hidden border border-slate-200 shadow-sm">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fliserens-overfladerenser-udstyr.png"
                  alt="Professionel fliserens og imprægnering med professionelt udstyr"
                  className="w-full h-80 sm:h-96 object-cover"
                />
              </div>
            </div>
          </div>

          {/* Row 2: Text Left, Image Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 lg:order-1 space-y-3.5 text-slate-700 leading-relaxed text-base">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                Giv dine gamle fliser nyt liv
              </h2>
              <p>
                Vind, vejr, fugt og alger sætter med tiden deres tydelige spor på udendørs fliser. En terrasse eller indkørsel, der tidligere fremstod lys og pæn, kan derfor med tiden blive mørk, grøn og trist at se på.
              </p>
              <p>
                Det betyder dog ikke nødvendigvis, at fliserne skal udskiftes. Med en professionel <strong>fliserens</strong> kan vi fjerne meget af den snavs og belægning, som har samlet sig på overfladen.
              </p>
              <p>
                Hos NF-Polering tilbyder vi fliserens til både private og erhverv og kan blandt andet rense:
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-slate-900 font-semibold text-sm">
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Terrasser</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Indkørsler</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Havegange</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Gangarealer</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Flisebelægninger omkring boligen</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Større udendørs flisearealer</span>
                </li>
              </ul>

              <p className="pt-2">
                Efter rensningen foretager vi <strong>algebehandling og imprægnering</strong>, som er inkluderet i vores pris fra <strong>29 kr. pr. m²</strong>. Det gør løsningen oplagt til dig, der gerne vil have gjort noget ved dine fliser uden at skulle investere i en helt ny belægning.
              </p>

              <div className="pt-3">
                <a
                  href="tel:30146070"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 lg:order-2">
              <div className="rounded-none overflow-hidden border border-slate-200 shadow-sm">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fliserens-ren-terrasse-have.png"
                  alt="Flot nyrenset fliseterrasse og have"
                  className="w-full h-80 sm:h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery */}
      <CmsGallery
        slug="fliserens"
        title="Billeder af fliserens &amp; imprægnering"
        subtitle="Se vores resultater samt nyrensede flisebelægninger på Vestsjælland."
      />

      {/* Reviews */}
      <ReviewSlider />
    </>
  );
}
